import { useState } from "react";
import { Link } from "react-router-dom";
import { z } from "zod";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { AFFILIATE_CATEGORIES, type AffiliateCategory } from "@/lib/affiliates";

const schema = z.object({
  applicant_name: z.string().trim().min(1).max(200),
  email: z.string().trim().email().max(320),
  organization: z.string().trim().max(200).optional().or(z.literal("")),
  category: z.enum(["freelance", "sourcing_production"]),
  services: z.string().trim().max(500),
  portfolio_url: z.string().trim().max(500).optional().or(z.literal("")),
  pitch: z.string().trim().min(10).max(4000),
  location: z.string().trim().max(120).optional().or(z.literal("")),
});

const AffiliateApply = () => {
  const [form, setForm] = useState({
    applicant_name: "",
    email: "",
    organization: "",
    category: "freelance" as AffiliateCategory,
    services: "",
    portfolio_url: "",
    pitch: "",
    location: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async () => {
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? "Please complete the required fields");
      return;
    }
    setLoading(true);
    try {
      const services = parsed.data.services
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      const { error } = await supabase.from("affiliate_applications").insert({
        applicant_name: parsed.data.applicant_name,
        email: parsed.data.email,
        organization: parsed.data.organization || null,
        category: parsed.data.category,
        services,
        portfolio_url: parsed.data.portfolio_url || null,
        pitch: parsed.data.pitch,
        location: parsed.data.location || null,
      });
      if (error) throw error;
      setSubmitted(true);
    } catch (e: any) {
      toast.error(e.message ?? "Could not submit application");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <section className="pt-36 pb-24">
        <div className="container mx-auto px-6 max-w-2xl">
          <Link to="/affiliates" className="text-sm text-primary hover:underline inline-flex items-center gap-1 mb-6">
            <ArrowLeft className="h-3 w-3" /> Back to Affiliates
          </Link>

          {submitted ? (
            <div className="bg-card border border-border rounded-xl p-10 text-center">
              <CheckCircle2 className="h-10 w-10 text-primary mx-auto mb-4" />
              <h1 className="font-display text-2xl font-bold mb-2">Application received</h1>
              <p className="text-muted-foreground mb-6">
                Thanks for applying. Our team will review your details and follow up by email
                within a few business days.
              </p>
              <Link to="/affiliates">
                <Button variant="outline">Back to Affiliates</Button>
              </Link>
            </div>
          ) : (
            <>
              <span className="text-primary text-sm font-semibold tracking-wider uppercase">Apply to Join</span>
              <h1 className="font-display text-3xl md:text-4xl font-bold mt-2 mb-3">
                Become an affiliate provider
              </h1>
              <p className="text-muted-foreground mb-8">
                Tell us about your work and we'll review your fit for the network. Listed providers
                receive direct project requests from our clients — no platform fees.
              </p>

              <div className="space-y-5 bg-card border border-border rounded-xl p-6">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm">Your name</Label>
                    <Input className="mt-1" value={form.applicant_name} onChange={(e) => setForm({ ...form, applicant_name: e.target.value })} maxLength={200} />
                  </div>
                  <div>
                    <Label className="text-sm">Email</Label>
                    <Input className="mt-1" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} maxLength={320} />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm">Organization (optional)</Label>
                    <Input className="mt-1" value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} maxLength={200} />
                  </div>
                  <div>
                    <Label className="text-sm">Location</Label>
                    <Input className="mt-1" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="e.g. Mobile, AL" maxLength={120} />
                  </div>
                </div>
                <div>
                  <Label className="text-sm">Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v as AffiliateCategory })}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {AFFILIATE_CATEGORIES.map((c) => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm">Services offered</Label>
                  <Input className="mt-1" value={form.services} onChange={(e) => setForm({ ...form, services: e.target.value })} placeholder="Comma-separated, e.g. Grant writing, Editing, Strategy" maxLength={500} />
                </div>
                <div>
                  <Label className="text-sm">Portfolio or website (optional)</Label>
                  <Input className="mt-1" value={form.portfolio_url} onChange={(e) => setForm({ ...form, portfolio_url: e.target.value })} placeholder="https://" maxLength={500} />
                </div>
                <div>
                  <Label className="text-sm">Tell us about your work</Label>
                  <Textarea className="mt-1" value={form.pitch} onChange={(e) => setForm({ ...form, pitch: e.target.value })} rows={6} maxLength={4000} placeholder="Recent projects, industries served, what makes you a fit." />
                </div>
                <Button onClick={submit} disabled={loading} className="w-full bg-primary text-primary-foreground">
                  {loading ? "Submitting…" : "Submit Application"}
                </Button>
              </div>
            </>
          )}
        </div>
      </section>
      <SiteFooter />
    </div>
  );
};

export default AffiliateApply;